import React, { Component, Suspense } from "react";
import PropTypes from "prop-types";
import { BrowserRouter as Router, Switch, matchPath } from "react-router-dom";
import { connect } from "react-redux";
import { authProtectedRoutes, publicRoutes } from "./routes/";
import AppRoute from "./routes/route";
import PageLoader from "./components/Common/PageLoader";

import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";
import { post } from "./helpers/api_helper";

import Swal from "sweetalert2";
import ThemeLoader from "./components/Common/ThemeLoader";
import "./assets/scss/rs-theme.css";

class App extends Component {
  idleTimeout = 20 * 60 * 1000; 
  idleTimer = null;
  refreshCookieCooldownMs = 5 * 60 * 1000;
  lastRefreshCookieAt = 0;
  isRefreshingCookie = false;
  activityEvents = ["mousemove", "keydown", "scroll", "click", "touchstart"];

  isNavigating = false;

  constructor(props) {
    super(props);
    this.state = {};
    this.getLayout = this.getLayout.bind(this);
  }

  componentDidMount() {
    document.title = "Agent portal - Realty Space";

    // Persist auth in sessionStorage so refresh keeps session,
    // but closing the tab/window logs out automatically.
    const sessionAuthUser = sessionStorage.getItem("authUser");
    const legacyAuthUser = localStorage.getItem("authUser");
    const authUser = sessionAuthUser || legacyAuthUser;

    // Migrate legacy localStorage authUser into sessionStorage once
    if (!sessionAuthUser && legacyAuthUser) {
      sessionStorage.setItem("authUser", legacyAuthUser);
      localStorage.removeItem("authUser");
    }

    this.setupIdleTimer();
    //document.addEventListener("visibilitychange", this.handleVisibilityChange);

    const pathname = window.location.pathname;

    // Allow all explicitly declared public routes (reset password, forgot password, etc.)
    const isPublicRoute = publicRoutes.some((route) =>
      matchPath(pathname, {
        path: route.path,
        exact: route.exact === true,
        strict: false,
      })
    );

    try {
      const user = JSON.parse(authUser);
      if (!user && !isPublicRoute) {
        window.location.href = "/login";
      }
    } catch (e) {
      if (!isPublicRoute) {
        window.location.href = "/login";
      }
    }
  }

  componentWillUnmount() {
    this.cleanupIdleTimer();
    //window.removeEventListener("beforeunload", this.handleWindowClose);
    //document.removeEventListener("visibilitychange", this.handleVisibilityChange);
  }

  // Detect if user is navigating rather than closing tab
  handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      this.isNavigating = true;
    } else {
      this.isNavigating = false;
    }
  };

  setupIdleTimer = () => {
    this.activityEvents.forEach((event) =>
      window.addEventListener(event, this.resetIdleTimer)
    );
    this.resetIdleTimer();
  };

  resetIdleTimer = () => {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(this.logoutUser, this.idleTimeout);
    this.refreshCookieIfNeeded();
  };

  refreshCookieIfNeeded = () => {
    const authUser =
      sessionStorage.getItem("authUser") || localStorage.getItem("authUser");
    if (!authUser) return;

    const pathname = window.location.pathname?.toLowerCase() || "";
    const isAuthPage =
      pathname === "/login" ||
      pathname === "/forgot-password" ||
      pathname === "/resetpassword";

    if (isAuthPage || this.isRefreshingCookie) return;

    const now = Date.now();
    if (now - this.lastRefreshCookieAt < this.refreshCookieCooldownMs) return;

    this.isRefreshingCookie = true;
    this.lastRefreshCookieAt = now;

   /* post("user/refresh-cookie", {})
      .catch(() => {
       
      })
      .finally(() => {
        this.isRefreshingCookie = false;
      });*/
  };

  cleanupIdleTimer = () => {
    this.activityEvents.forEach((event) =>
      window.removeEventListener(event, this.resetIdleTimer)
    );
    if (this.idleTimer) clearTimeout(this.idleTimer);
  };

  logoutUser = () => {
    const pathname = window.location.pathname?.toLowerCase() || "";
    const isAuthPage =
      pathname === "/login" ||
      pathname === "/forgot-password" ||
      pathname === "/resetpassword";

    if (isAuthPage) {
      localStorage.clear();
    } else {
      Swal.fire({
        title: "Session Expired",
        text: "You have been logged out due to inactivity.",
        icon: "warning",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        localStorage.clear();
        window.location.href = "/login";
      });
    }
  };

  getLayout = () => {
    let layoutCls = VerticalLayout;
    switch (this.props.layout.layoutType) {
      case "horizontal":
        layoutCls = HorizontalLayout;
        break;
      default:
        layoutCls = VerticalLayout;
        break;
    }
    return layoutCls;
  };

  render() {
    const Layout = this.getLayout();

    return (
      <React.Fragment>
        <Router>
          <ThemeLoader />
          <Suspense fallback={<PageLoader />}>
            <Switch>
              {publicRoutes.map((route, idx) => (
                <AppRoute
                  path={route.path}
                  exact={route.exact}
                  layout={NonAuthLayout}
                  component={route.component}
                  key={idx}
                  isAuthProtected={false}
                />
              ))}

              {authProtectedRoutes.map((route, idx) => (
                <AppRoute
                  path={route.path}
                  exact={route.exact}
                  layout={Layout}
                  component={route.component}
                  key={idx}
                  isAuthProtected={true}
                />
              ))}
            </Switch>
          </Suspense>
        </Router>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  layout: state.Layout,
});

App.propTypes = {
  layout: PropTypes.object,
};

export default connect(mapStateToProps, null)(App);
